import React from 'react';
import { Plus } from 'lucide-react';
import { useActiveIecMaterialTypes } from '../../hooks/admin/iecmaterialTypes/useActiveIecMaterialTypes';
import { useActiveIecMaterialFormats } from '../../hooks/admin/iecMaterialFormats/useActiveIecMaterialFormats';

const INPUT_CLASS =
  'w-full rounded-lg px-2 py-1.5 text-xs text-slate-700 border border-gray-300 bg-white shadow-sm focus:border-primary-dark focus:outline-none focus:ring-1 focus:ring-primary-dark/40 transition';
const SELECT_CLASS =
  'w-full rounded-lg px-2 py-1.5 text-xs text-slate-700 border border-gray-300 bg-white shadow-sm focus:border-primary-dark focus:outline-none focus:ring-1 focus:ring-primary-dark/40 transition';

const IecMaterialsTypeFormatQuantity = ({
  label = 'IEC Materials',
  required = false,
  value,
  onChange,
  disabled = false,
  error,
  enabled = true,
}) => {
  const { iecMaterialTypes = [], isLoading: isLoadingTypes } = useActiveIecMaterialTypes({ enabled });
  const { iecMaterialFormats = [], isLoading: isLoadingFormats } = useActiveIecMaterialFormats({ enabled });

  const rows = Array.isArray(value) && value.length
    ? value
    : [{ type: '', format: '', quantity: '' }];

  const updateRow = (index, updates) => {
    if (!onChange || disabled) return;
    const next = rows.map((row, i) => (i === index ? { ...row, ...updates } : row));
    onChange(next);
  };

  const addRow = () => {
    if (!onChange || disabled) return;
    onChange([...rows, { type: '', format: '', quantity: '' }]);
  };

  const removeRow = (index) => {
    if (!onChange || disabled || rows.length <= 1) return;
    const next = rows.filter((_, i) => i !== index);
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-primary-dark">
        {label} {required && <span className="text-red-500">*</span>}
      </h3>

      <div className="space-y-2">
        {rows.map((row, index) => {
          const isLast = index === rows.length - 1;
          const canRemove = rows.length > 1;

          return (
            <div
              key={index}
              className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_8rem_auto]"
            >
              <select
                value={row.type ?? ''}
                onChange={(e) => updateRow(index, { type: e.target.value })}
                disabled={disabled || isLoadingTypes}
                className={SELECT_CLASS}
                aria-label="IEC material type"
              >
                <option value="">{isLoadingTypes ? 'Loading…' : 'Select type...'}</option>
                {iecMaterialTypes.map((t) => (
                  <option key={t._id} value={t._id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <select
                value={row.format ?? ''}
                onChange={(e) => updateRow(index, { format: e.target.value })}
                disabled={disabled || isLoadingFormats}
                className={SELECT_CLASS}
                aria-label="IEC material format"
              >
                <option value="">{isLoadingFormats ? 'Loading…' : 'Select format...'}</option>
                {iecMaterialFormats.map((f) => (
                  <option key={f._id} value={f._id}>
                    {f.name}
                  </option>
                ))}
              </select>
              <input
                type="number"
                value={row.quantity ?? ''}
                onChange={(e) => updateRow(index, { quantity: e.target.value })}
                placeholder="Qty"
                disabled={disabled}
                className={INPUT_CLASS}
                aria-label="IEC material quantity"
                min="0"
              />
              <div className="flex items-center justify-end">
                {isLast ? (
                  <button
                    type="button"
                    onClick={addRow}
                    disabled={disabled}
                    className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-lg text-primary-dark border border-gray-300 bg-white hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                    aria-label="Add IEC material row"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => removeRow(index)}
                    disabled={disabled || !canRemove}
                    className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-lg text-red-600 border border-gray-300 bg-white hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                    aria-label="Remove IEC material row"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
};

export default IecMaterialsTypeFormatQuantity;

